import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import CopyTextButton from './CopyTextButton.jsx';

/** Text-only Preview edit: no generation, media or scheduling controls. */
export default function PreviewCaptionEditor({ raw, caption, onCancel, onSaved, onConflict }) {
  const { t } = useTranslation();
  const [text, setText] = useState(caption);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const savingRef = useRef(false);
  // Keep the version the user actually started editing, even if a focus
  // refresh arrives. A stale save must never overwrite the newer version.
  const [base] = useState(() => ({
    id: raw.contentItem.id,
    brandProfileId: raw.contentItem.brandProfileId,
    expectedVersionId: raw.activeVersion.id,
  }));
  const valid = text.trim().length > 0 && text.length <= 10000;
  const save = async (event) => {
    event.preventDefault();
    if (savingRef.current || !valid || text === caption || error === 'captionConflict') return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await api.content.createManualEdit(base.id, {
        caption: text,
        expectedVersionId: base.expectedVersionId,
        brandProfileId: base.brandProfileId,
        requireUnscheduledDraft: true,
      });
    } catch (err) {
      const conflict = err?.status === 400 && err?.code === 'VALIDATION_ERROR';
      setError(conflict ? 'captionConflict' : 'captionFailed');
      if (conflict) onConflict?.();
      savingRef.current = false;
      setSaving(false);
      return;
    }
    savingRef.current = false;
    setSaving(false);
    onSaved();
  };
  return (
    <form className="card fp-caption-editor" onSubmit={save} aria-busy={saving}>
      <label className="fpb-card-h" htmlFor="preview-caption">{t('freePreview.captionTitle')}</label>
      <p className="fpb-muted" id="preview-caption-help">{t('freePreview.captionHelp')}</p>
      <textarea id="preview-caption" autoFocus rows={9} maxLength={10000} value={text}
        aria-describedby="preview-caption-help" disabled={saving}
        onChange={(event) => setText(event.target.value)} />
      {error && <div role="alert"><p>{t(`freePreview.${error}`)}</p><CopyTextButton text={text} /></div>}
      <div className="fpb-next-row">
        <button type="submit" className="btn btn-primary" disabled={saving || !valid || text === caption || error === 'captionConflict'}>
          {t(saving ? 'freePreview.captionSaving' : 'freePreview.captionSave')}
        </button>
        <button type="button" className="btn btn-secondary" disabled={saving} onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}
